/**
 * Brand Safety report helpers — all pure, all null-safe.
 *
 * Everything the report renders goes through one of these so the view layer never has to defend
 * against a missing field. When the real API lands, `normalizeBrandSafetyReport` is the seam: adapt
 * the response there and the components keep working unchanged.
 */

import moment from 'moment';
import {
  ANALYSIS_STATE,
  ANALYSIS_STATE_BY_CHECK_STATUS,
  DASH,
  REVIEW_STATUS,
  REVIEW_STATUS_ORDER,
  SEVERITY,
  SEVERITY_BY_LEVEL,
  SEVERITY_ORDER,
} from './brandSafetyConstants';
import { iconKeyForCategory } from './brandSafetyIcons';
// The flag-type grouping reuses campaign creation's canonical sections (single source of truth).
import { BRAND_SAFETY_SEVERITY_SECTIONS } from '../../Persona/BrandSafety/brandSafetyConsts';

// ─── Formatters ───────────────────────────────────────────────────────────────

/** `2026-07-19` → `July 19, 2026`. Returns null for anything unparseable. */
export function formatAnalysisDate(value) {
  if (!value) return null;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('MMMM D, YYYY') : null;
}

/** `2012-11-07` → `07.11.2012`, the compact axis caption format used on the timeline. */
export function formatAxisDate(value) {
  if (!value) return '';
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '';
}

/**
 * Human label for one timeline bucket's period, matched to the bucket interval (see
 * `bucketPublishTimeline`): month → "Jun 2024", week → "Week of Jun 3, 2024", day → "Jun 3, 2024".
 * Used in the bar hover tooltip. Null/invalid → ''.
 */
export function formatTimelineBucketLabel(ts, interval) {
  if (ts == null) return '';
  const parsed = moment(ts);
  if (!parsed.isValid()) return '';
  if (interval === 'month') return parsed.format('MMM YYYY');
  if (interval === 'week') return `Week of ${parsed.format('MMM D, YYYY')}`;
  return parsed.format('MMM D, YYYY');
}

/** `2689` → `2,689`. Non-numeric input yields the dash placeholder. */
export function formatCount(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return DASH;
  return num.toLocaleString('en-US');
}

/** `560000` → `560K`. Already-formatted strings (`'560K'`) pass through untouched. */
export function formatFollowers(value) {
  if (typeof value === 'string' && value.trim() !== '') return value;
  const num = Number(value);
  if (!Number.isFinite(num)) return DASH;
  if (num >= 1_000_000) return `${Math.round((num / 1_000_000) * 10) / 10}M`;
  if (num >= 1_000) return `${Math.round(num / 1_000)}K`;
  return String(num);
}

/** `9.12` → `9.12%`. Accepts a preformatted string too. */
export function formatPercent(value) {
  if (typeof value === 'string' && value.trim() !== '') {
    return value.endsWith('%') ? value : `${value}%`;
  }
  const num = Number(value);
  if (!Number.isFinite(num)) return DASH;
  return `${Math.round(num * 100) / 100}%`;
}

/** A cell that should read as "no value" rather than "zero". */
export function formatOptionalCount(value) {
  if (value === null || value === undefined || value === '') return DASH;
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return DASH;
  return String(num);
}

/** First letter of a brand name, for the coloured initial tile. */
export function brandInitial(name) {
  const trimmed = String(name ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
}

/**
 * Brand logo URL from a brand name — the "Brand Affinity" approach used on the creator details page
 * (Persona/CreatorDetailsPage/CreatorDetailsHashtagBrands.jsx): guess `<brandname>.com` and ask
 * Google's favicon service. Best-effort — an unmatched domain 404s, so callers must provide an
 * initials fallback. Returns '' for an empty name.
 */
export function brandFaviconUrl(name) {
  const domain = String(name ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();
  return domain ? `https://www.google.com/s2/favicons?domain=${domain}.com&sz=128` : '';
}

// ─── Aggregations ─────────────────────────────────────────────────────────────

/**
 * Turn `{ severe: 5, moderate: 2, low: 1, minor: 2 }` into the segments the severity bar renders,
 * dropping empty buckets so a zero never shows as a sliver.
 *
 * @returns {Array<{ key: string, count: number, flexGrow: number }>}
 */
export function toSeverityBarSegments(counts = {}) {
  return SEVERITY_ORDER.map((key) => ({ key, count: Number(counts?.[key]) || 0 }))
    .filter((seg) => seg.count > 0)
    .map((seg) => ({ ...seg, flexGrow: seg.count }));
}

/** Total detections across every severity bucket of one flag-type group. */
export function totalSeverityCount(counts = {}) {
  return SEVERITY_ORDER.reduce((sum, key) => sum + (Number(counts?.[key]) || 0), 0);
}

/**
 * The three status segments for the pending/approved/rejected bar, as flex-weighted parts.
 * Zero-count statuses are dropped, so a report with nothing rejected renders a clean two-tone bar.
 */
export function toStatusBarSegments(statusCounts = {}) {
  return REVIEW_STATUS_ORDER.map((key) => ({ key, count: Number(statusCounts?.[key]) || 0 }))
    .filter((seg) => seg.count > 0)
    .map((seg) => ({ ...seg, flexGrow: seg.count }));
}

/**
 * Highest severity present in a bucket map, or null when the group is entirely empty.
 * Used to tint the flag-type row icon.
 */
export function highestSeverity(counts = {}) {
  return SEVERITY_ORDER.find((key) => (Number(counts?.[key]) || 0) > 0) ?? null;
}

/**
 * Timeline as a per-post REVIEW-STATUS histogram: one bar per analyzed post, coloured by status —
 * approved → green, rejected → red, everything else → pending/blue. Counts come from `statusCounts`
 * (which sum to `analyzedCount`), so the mix is accurate. The payload carries no per-post score or
 * publish date, so bar heights and order are representative (deterministic, not chronological); the
 * three statuses are round-robined so the colours scatter across the width instead of clustering.
 *
 * @returns {Array<{ key: string, status: string, heightPct: number }>}
 */
export function toStatusTimelineBars(statusCounts = {}, analyzedCount = 0) {
  const approved = Math.max(0, Math.round(Number(statusCounts?.approved) || 0));
  const rejected = Math.max(0, Math.round(Number(statusCounts?.rejected) || 0));
  const total = Math.max(Math.round(Number(analyzedCount) || 0), approved + rejected);
  if (total <= 0) return [];
  const pending = Math.max(0, total - approved - rejected);

  const statuses = [REVIEW_STATUS.APPROVED, REVIEW_STATUS.PENDING, REVIEW_STATUS.REJECTED];
  const remaining = [approved, pending, rejected];
  const order = [];
  while (order.length < total) {
    for (let i = 0; i < statuses.length; i += 1) {
      if (remaining[i] > 0) {
        order.push(statuses[i]);
        remaining[i] -= 1;
      }
    }
  }
  // Deterministic height variation (no Math.random, so renders are stable) for the histogram look.
  return order.map((status, i) => ({ key: `t-${i}`, status, heightPct: 45 + ((i * 29) % 55) }));
}

const BS_TIMELINE_DAY_MS = 24 * 60 * 60 * 1000;

/**
 * All analyzed posts as timeline points for the publish-date chart: `{ ts, flagged }` per post that has a
 * parseable `publishDate`, sorted oldest-first. Built from the FULL `check.posts` (not the flagged-only
 * `report.posts`) since the timeline covers all analyzed content. Posts without a valid date are dropped.
 *
 * @returns {Array<{ ts: number, flagged: boolean }>}
 */
export function buildPublishTimeline(posts = []) {
  if (!Array.isArray(posts)) return [];
  return posts
    .map((post) => {
      const parsed = post?.publishDate ? moment(post.publishDate) : null;
      return parsed && parsed.isValid()
        ? { ts: parsed.valueOf(), flagged: Boolean(post?.flagged) }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.ts - b.ts);
}

/**
 * Aggregate publish-date points (from `buildPublishTimeline`) into a bounded set of chronological buckets,
 * so thousands of posts render as a readable strip instead of overlapping. The interval adapts to the span
 * — day (≤ ~45d), week (≤ ~2y), else month — via moment `startOf`. Each bucket carries its post count and
 * how many were flagged; `heightPct` scales the busiest bucket to full height with a visible floor.
 * `oldestTs` is the exact timestamp of the earliest post — it drives the left axis label.
 *
 * @returns {{ buckets: Array<{ key, ts, count, flaggedCount, hasFlagged, heightPct }>, oldestTs, interval }}
 */
export function bucketPublishTimeline(timeline = []) {
  if (!Array.isArray(timeline) || timeline.length === 0) {
    return { buckets: [], oldestTs: null, interval: null };
  }

  const oldestTs = timeline[0].ts;
  const newestTs = timeline[timeline.length - 1].ts;
  // Interval tracks the CONTENT's own date range (oldest → newest post), not "up to today" — so old
  // catalogs don't collapse to one coarse bar with a long empty tail. The axis's "Today" is a label.
  const spanDays = (newestTs - oldestTs) / BS_TIMELINE_DAY_MS;

  let interval = 'day';
  if (spanDays > 730) interval = 'month';
  else if (spanDays > 45) interval = 'week';

  const byBucket = new Map();
  timeline.forEach((point) => {
    const bucketTs = moment(point.ts)
      .startOf(interval)
      .valueOf();
    const entry = byBucket.get(bucketTs) || { ts: bucketTs, count: 0, flaggedCount: 0 };
    entry.count += 1;
    if (point.flagged) entry.flaggedCount += 1;
    byBucket.set(bucketTs, entry);
  });

  const sorted = [...byBucket.values()].sort((a, b) => a.ts - b.ts);
  const maxCount = sorted.reduce((max, b) => Math.max(max, b.count), 0) || 1;
  const buckets = sorted.map((b) => ({
    key: `t-${b.ts}`,
    ts: b.ts,
    count: b.count,
    flaggedCount: b.flaggedCount,
    hasFlagged: b.flaggedCount > 0,
    // Floor at 12% so a single-post bucket stays visible; scale the rest up to full height.
    heightPct: Math.round(12 + (b.count / maxCount) * 88),
  }));

  return { buckets, oldestTs, interval };
}

// ─── Check-report derivations ─────────────────────────────────────────────────
// The API "check report object" (endpoints 5/6) carries per-post `safetyDetails` and a flat
// `categorySummary`; the report's aggregate widgets are derived from those here.

/** Raw check `status` → one of the three UI analysis states. */
export function mapCheckStatus(status) {
  return ANALYSIS_STATE_BY_CHECK_STATUS[status] ?? ANALYSIS_STATE.PROCESSING;
}

/** Raw ML level (`L1`–`L4`) → severity bucket. Unknown → minor. */
export function severityFromLevel(level) {
  return SEVERITY_BY_LEVEL[level] ?? SEVERITY.MINOR;
}

/**
 * Category name from a flag key: strip the modality prefix and the `Flag` suffix.
 * `videoAlcoholFlag` → `Alcohol`, `textTrademarkFlag` → `Trademark`, `AlcoholFlag` → `Alcohol`.
 */
export function flagToCategory(flagName) {
  if (!flagName) return null;
  const core = String(flagName)
    .replace(/^(text|image|video|transcript)/i, '')
    .replace(/Flag$/i, '');
  if (!core) return null;
  return core.charAt(0).toUpperCase() + core.slice(1);
}

/** The `*Flag` key set true on a detection entry (e.g. `videoAlcoholFlag`), or null. */
function flagKeyOf(entry) {
  if (!entry || typeof entry !== 'object') return null;
  return Object.keys(entry).find((k) => /Flag$/.test(k) && entry[k]) ?? null;
}

/** Category → display label. Only categories whose display differs from the raw name are listed. */
const CATEGORY_LABEL = { Smoking: 'Tobacco/Nicotine', Therapeutics: 'Therapeutic Drugs' };
const categoryLabel = (category) => CATEGORY_LABEL[category] ?? category;

// Deterministic tile colours for the trademark-by-brand table (assigned by first-seen order).
const BRAND_TILE_COLORS = [
  '#0e6b3e',
  '#c8102e',
  '#0e4c8a',
  '#2f3237',
  '#111318',
  '#5a6a75',
  '#3a3d42',
  '#b02a24',
  '#8a3230',
  '#1b4b9b',
];

/** Deterministic tile colour for a brand name (stable across renders; used by the initials fallback). */
export function brandTileColor(name) {
  const str = String(name ?? '');
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash + str.charCodeAt(i)) % BRAND_TILE_COLORS.length;
  }
  return BRAND_TILE_COLORS[hash];
}

/**
 * Walk every analyzed post's `safetyDetails` and collect flagged detections (`safetyScore < 100`)
 * plus trademark-brand placements. One pass feeds all the aggregate widgets.
 *
 * Reads from `check.posts` (ep9 returns every analyzed post, each carrying a `flagged` boolean) rather
 * than the old `check.flaggedPosts` (a pre-filtered array); filtering on `flagged` here keeps this the
 * same set the old field used to hand over.
 *
 * @returns {{
 *   detections: Array<{ category: string, severity: string, postId: string }>,
 *   brands: Array<{ brand: string, placement: string }>,
 * }}
 */
export function collectCheckDetections(check) {
  const posts = Array.isArray(check?.posts) ? check.posts.filter((p) => p?.flagged) : [];
  const detections = [];
  const brands = [];

  const brandOf = (raw) => String(raw ?? '').replace(/\s*Logo$/i, '') || null;

  posts.forEach((post) => {
    const postId = post?.creatorPostId ?? '';
    const details = post?.safetyDetails ?? {};

    // text[] — legacy path: category flag + trademarkBrands with placementLevel.
    (Array.isArray(details.text) ? details.text : []).forEach((entry) => {
      if (Number(entry?.safetyScore) >= 100) return;
      const category = flagToCategory(flagKeyOf(entry));
      if (category) {
        detections.push({ category, severity: severityFromLevel(entry.severityLevel), postId });
      }
      (Array.isArray(entry?.trademarkBrands) ? entry.trademarkBrands : []).forEach((b) => {
        if (b?.brand) brands.push({ brand: b.brand, placement: b.placementLevel ?? null });
      });
    });

    // image[] — one detection per imageDetections entry; trademark brands from label/brand. A
    // trademark entry with no bounding boxes yet (real API sends this) instead carries brand hits in
    // `trademarkBrands[]` (mirroring the text section) — one detection per brand there.
    (Array.isArray(details.image) ? details.image : []).forEach((img) => {
      (Array.isArray(img?.imageDetails) ? img.imageDetails : []).forEach((entry) => {
        if (Number(entry?.safetyScore) >= 100) return;
        const category = flagToCategory(flagKeyOf(entry));
        if (!category) return;
        const severity = severityFromLevel(entry.severityLevel);
        const dets = Array.isArray(entry?.imageDetections) ? entry.imageDetections : [];
        const trademarkBrands = Array.isArray(entry?.trademarkBrands) ? entry.trademarkBrands : [];
        if (category === 'Trademark' && !dets.length && trademarkBrands.length) {
          trademarkBrands.forEach((b) => {
            detections.push({ category, severity, postId });
            if (b?.brand) brands.push({ brand: b.brand, placement: b.placementLevel ?? null });
          });
          return;
        }
        (dets.length ? dets : [null]).forEach((det) => {
          detections.push({ category, severity, postId });
          if (category === 'Trademark') {
            const brand = brandOf(det?.brand || det?.label);
            if (brand) brands.push({ brand, placement: null });
          }
        });
      });
    });

    // video[] — one detection per detected object (bounding box), matching the image branch above so
    // "objects" means the same thing in every modality. A flagged entry with no boxes still counts once.
    (Array.isArray(details.video) ? details.video : []).forEach((v) => {
      (Array.isArray(v?.videoDetails) ? v.videoDetails : []).forEach((d) => {
        if (Number(d?.safetyScore) >= 100) return;
        const category = flagToCategory(flagKeyOf(d));
        if (!category) return;
        const severity = severityFromLevel(d.severityLevel);
        const dets = Array.isArray(d?.videoDetections) ? d.videoDetections : [];
        (dets.length ? dets : [null]).forEach((det) => {
          detections.push({ category, severity, postId });
          if (category === 'Trademark') {
            const brand = brandOf(det?.brand || det?.label || d?.label);
            if (brand) brands.push({ brand, placement: null });
          }
        });
      });
    });

    (Array.isArray(details.transcript) ? details.transcript : []).forEach((entry) => {
      if (Number(entry?.safetyScore) >= 100) return;
      const category = flagToCategory(flagKeyOf(entry));
      if (category) {
        detections.push({ category, severity: severityFromLevel(entry.severityLevel), postId });
      }
    });
  });

  return { detections, brands };
}

/** Severity summary cards: count flagged detections per severity bucket. */
export function deriveSeverityObjects(detections = []) {
  const counts = SEVERITY_ORDER.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
  detections.forEach((d) => {
    if (counts[d.severity] !== undefined) counts[d.severity] += 1;
  });
  return SEVERITY_ORDER.map((key) => ({ key, objects: counts[key] }));
}

/**
 * Object Detection table: one row per (subclass, severity) pair found in the walked detections.
 *
 * A subclass that spans severities gets a row per severity rather than collapsing to its worst — so
 * "Trademark · Severe · 454" no longer implies every Trademark detection is severe (it was only the
 * highest severity present). Rows are ordered by severity (severe first, across all subclasses), so
 * the most severe findings lead regardless of which subclass they belong to.
 *
 * Counted from the SAME `detections` array as the severity cards and the flag-type bars, so all three
 * widgets total the same number. It used to take its count column from the API's `categorySummary`,
 * which counts one per *flag* while the walk counts one per *detected object* — that mismatch is why
 * the cards could read "8 objects" beside an Object Detection table totalling 4. Deriving from the
 * walk also means a category the ML omitted from `categorySummary` can no longer vanish from this
 * table (nor a summary-only category appear here as a phantom row with 0 posts).
 */
export function deriveObjectDetections(detections = []) {
  const byGroup = {};

  detections.forEach((d) => {
    const groupKey = `${d.category}::${d.severity}`;
    if (!byGroup[groupKey]) {
      byGroup[groupKey] = {
        category: d.category,
        severity: d.severity,
        count: 0,
        posts: new Set(),
      };
    }
    const row = byGroup[groupKey];
    row.count += 1;
    row.posts.add(d.postId);
  });

  return (
    Object.values(byGroup)
      .map((g) => ({
        key: `${g.category}::${g.severity}`,
        label: categoryLabel(g.category),
        icon: iconKeyForCategory(g.category) ?? null,
        severity: g.severity ?? SEVERITY.MINOR,
        detections: g.count,
        // Distinct posts carrying this subclass AT THIS SEVERITY — a post that spans two severities of
        // the same subclass counts in both rows, matching the finer grouping.
        images: g.posts.size,
      }))
      // Severity first (severe rows lead across every subclass), then detections desc, then label for a
      // stable tie-break.
      .sort((a, b) => {
        const sevDiff = SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity);
        if (sevDiff !== 0) return sevDiff;
        if (b.detections !== a.detections) return b.detections - a.detections;
        return a.label.localeCompare(b.label);
      })
  );
}

// category → canonical section title, derived once from the campaign-creation sections.
const CATEGORY_SECTION = (() => {
  const map = {};
  (BRAND_SAFETY_SEVERITY_SECTIONS || []).forEach((section) => {
    (section.items || []).forEach((flagKey) => {
      const category = flagToCategory(flagKey);
      if (category) map[category] = section.title;
    });
  });
  return map;
})();
const SECTION_ORDER = (BRAND_SAFETY_SEVERITY_SECTIONS || []).map((s) => s.title);

// section title → its member category display labels, from the same source of truth. Used to expand a
// Flag-Type section into the categories the Review tab filters on.
const SECTION_CATEGORY_LABELS = (() => {
  const map = {};
  (BRAND_SAFETY_SEVERITY_SECTIONS || []).forEach((section) => {
    map[section.title] = (section.items || [])
      .map((flagKey) => categoryLabel(flagToCategory(flagKey)))
      .filter(Boolean);
  });
  return map;
})();

/**
 * Resolve a Report→Review focus into the category labels to select in Review. A flag-type SECTION
 * title expands to its member categories (the Review's category filter ORs, so a post in any of them
 * matches); a value that is not a section (a single category label) passes through unchanged. Severity
 * focuses are handled separately by the caller (they take the severity branch).
 */
export function categoriesForFocusGroup(focusedGroup) {
  if (!focusedGroup) return [];
  return SECTION_CATEGORY_LABELS[focusedGroup] ?? [focusedGroup];
}

/**
 * A raw flag key (`TrademarkFlag`) → the focus group to open Review on: its category display label,
 * which `categoriesForFocusGroup` passes straight through. Use this rather than the column's flag
 * labels — those spell some categories differently (`Trademark Detection` vs `Trademark`).
 */
export function focusGroupForFlagKey(flagKey) {
  const category = flagToCategory(flagKey);
  return category ? categoryLabel(category) : null;
}

// ─── Brand Safety Parameters (Filters drawer) — client-side filtering ─────────
// The drawer holds `{ [Flag]: { severe, moderate, low, minor } }` (campaign-creation shape). A
// detection maps to `${rawCategory}Flag` + its severity bucket and is hidden when that leaf is false.
// An absent flag counts as enabled, so the default (all-on) map filters nothing.
// TODO(BE): the same parameters will drive filtering server-side; this is the client-side stand-in.

/** True when any flag/severity leaf is off, i.e. filtering would change what shows. */
export function preferencesActive(filters) {
  if (!filters || typeof filters !== 'object') return false;
  return Object.values(filters).some(
    (sev) => sev && typeof sev === 'object' && Object.values(sev).some((v) => v === false)
  );
}

/** Is this (rawCategory, severity) enabled by the parameters? Unknown flag ⇒ enabled. */
export function isFlagSeverityEnabled(filters, rawCategory, severity) {
  const flag = filters?.[`${rawCategory}Flag`];
  if (!flag) return true;
  return flag[severity] !== false;
}

/** Keep only detections (from `collectCheckDetections`) whose flag+severity is enabled. */
export function filterDetectionsByPreferences(detections = [], filters) {
  if (!preferencesActive(filters)) return detections;
  return detections.filter((d) => isFlagSeverityEnabled(filters, d.category, d.severity));
}

/** A Review post survives if any of its objects/segments/trademarks stays enabled under the filters. */
export function postPassesPreferences(post, filters) {
  if (!preferencesActive(filters)) return true;
  const items = [
    ...(post.objects || []),
    ...(post.segments || []),
    ...(post.trademarks || []).map((t) => ({
      rawCategory: 'Trademark',
      severity: t.severity ?? post.severity,
    })),
  ];
  if (!items.length) return true;
  return items.some((d) => isFlagSeverityEnabled(filters, d.rawCategory, d.severity));
}

// ─── Find Similar (shared across every Brand Safety screen) ───────────────────
// "Similar" = same post severity AND at least one shared raw flag category.

/** The set of raw flag categories a normalized post carries (objects + trademarks + video segments). */
export function postRawCategories(post) {
  const set = new Set();
  (post?.objects || []).forEach((d) => d.rawCategory && set.add(d.rawCategory));
  (post?.trademarks || []).forEach((d) => set.add(d.rawCategory || 'Trademark'));
  (post?.segments || []).forEach((s) => s.rawCategory && set.add(s.rawCategory));
  return set;
}

// ─── Per-detection review (AAPU-6775) ─────────────────────────────────────────

/**
 * Every FLAGGED ITEM a normalized post carries, in the order the Review rail renders them: image /
 * caption objects, then trademarks, then video segments. This is the unit the AI Findings rail
 * approves individually, and the set the post-level verdict waits on.
 */
export function postDetections(post) {
  return [...(post?.objects || []), ...(post?.trademarks || []), ...(post?.segments || [])];
}

/** The stable per-detection keys of `postDetections(post)` — the identity the review overlay is keyed by. */
export function postDetectionKeys(post) {
  return postDetections(post)
    .map((d) => d.key)
    .filter(Boolean);
}

/**
 * The verdicts already persisted on a post's flagged items, as `{ [key]: 'approved'|'rejected' }`.
 * These come back on the check payload (`detection.reviewAction`, stamped by `normalizeReviewPost`)
 * from reviews done in a prior session, so a post whose remaining items are cleared now can still be
 * recognised as fully resolved even though those earlier verdicts never entered this session's overlay.
 */
export function postPersistedReviewActions(post) {
  const map = {};
  postDetections(post).forEach((d) => {
    if (d?.key && d.reviewAction) map[d.key] = d.reviewAction;
  });
  return map;
}

/**
 * A stable identity for a flagged item, built from the tuple the backend persists a verdict against —
 * `(source, category, label, brand, timeInSeconds)`. The FE's per-render `key` is positional and
 * unstable, so a stored `detectionReviews[]` verdict is matched to a rendered detection by THIS tuple
 * instead (see `BrandSafetyDetectionIdentity` on the backend). Normalization mirrors the backend:
 * `source` is case-folded, the text fields are trimmed, a missing field collapses to '' and a
 * non-numeric time to 'null' (0 is a real timestamp and is kept).
 */
export function detectionIdentityKey({ source, category, label, brand, timeInSeconds } = {}) {
  const text = (v) => (v == null ? '' : String(v).trim());
  const time =
    timeInSeconds == null || timeInSeconds === '' || Number.isNaN(Number(timeInSeconds))
      ? 'null'
      : String(Number(timeInSeconds));
  return [text(source).toLowerCase(), text(category), text(label), text(brand), time].join('|');
}

/**
 * A stable identity for a FLAG — the `(category, label, brand)` tuple the occurrences endpoints and
 * the approve-all endpoints are filtered by. `source` and `timeInSeconds` are deliberately excluded:
 * a flag SPANS them, so an "Apple TV" trademark in an image and one in a caption at 0:42 are the
 * same flag and must both be swept by one "Approve all". Normalization matches
 * `detectionIdentityKey` (trim, missing collapses to '') so the two agree on what "same text" means,
 * plus a case-fold so a rendered detection matches the flag the reviewer clicked whatever the ML
 * casing was.
 *
 * Used by `approveAllReviewDetections` to pick which loaded detections its optimistic overlay covers:
 * the server sweeps the whole scope, so the FE has to recognise every loaded occurrence of the flag,
 * not just the one whose card opened the modal.
 */
export function flagIdentityKey({ rawCategory, category, label, brand } = {}) {
  const text = (v) => (v == null ? '' : String(v).trim()).toLowerCase();
  return [text(rawCategory ?? category), text(label), text(brand)].join('|');
}

/**
 * Index a post's persisted per-detection verdicts (`post.detectionReviews[]` from endpoint 6) by their
 * tuple identity, so `normalizeReviewPost` can stamp each rendered detection with its stored action.
 * @returns {Map<string, 'approved'|'rejected'>}
 */
export function buildDetectionReviewIndex(detectionReviews) {
  const index = new Map();
  (Array.isArray(detectionReviews) ? detectionReviews : []).forEach((r) => {
    if (!r?.action) return;
    index.set(
      detectionIdentityKey({
        source: r.source,
        category: r.category,
        label: r.label,
        brand: r.brand,
        timeInSeconds: r.timeInSeconds,
      }),
      r.action
    );
  });
  return index;
}

/** The stored verdict for one normalized detection, or null. `d.rawCategory`/`d.start` are the tuple's
 * `category`/`timeInSeconds` (matching what the review write path sends). */
function detectionReviewAction(index, d) {
  if (!index || !index.size) return null;
  return (
    index.get(
      detectionIdentityKey({
        source: d.source,
        category: d.rawCategory,
        label: d.label,
        brand: d.brand,
        timeInSeconds: d.start,
      })
    ) ?? null
  );
}

/** True when `post` shares the reference's post severity AND at least one raw flag category. */
export function isSimilarPost(post, reference) {
  if (!post || !reference) return false;
  // A post is always similar to itself, so a selected item never vanishes — even one with zero graded
  // detections (e.g. a text-only/boxless flag), whose category set would otherwise be empty.
  if (post.id != null && post.id === reference.id) return true;
  if (post.severity !== reference.severity) return false;
  const refCats = postRawCategories(reference);
  if (!refCats.size) return false;
  return [...postRawCategories(post)].some((c) => refCats.has(c));
}

/**
 * Ids of the posts similar to ANY of the reference posts (union) — powers the "Find Similar" filter.
 * Returns `null` when there are no references (the same no-op sentinel as `passedPostIds`), so a caller
 * can short-circuit. A reference post matches itself, so the selected items stay visible.
 */
export function filterSimilarPostIds(posts = [], references = []) {
  const refs = (Array.isArray(references) ? references : []).filter(Boolean);
  if (!refs.length) return null;
  const ids = new Set();
  (Array.isArray(posts) ? posts : []).forEach((p) => {
    if (refs.some((ref) => isSimilarPost(p, ref))) ids.add(p.id);
  });
  return ids;
}

/**
 * Flag Type table: group detections into the canonical sections. Each group carries a severity
 * distribution (for its bar), an image count and an icon. Empty sections are dropped.
 */
export function deriveFlagTypeGroups(detections = []) {
  const groups = {};
  detections.forEach((d) => {
    const section = CATEGORY_SECTION[d.category] ?? 'Other';
    if (!groups[section]) {
      groups[section] = { severityCounts: {}, posts: new Set(), iconCategory: null };
    }
    const g = groups[section];
    g.severityCounts[d.severity] = (g.severityCounts[d.severity] || 0) + 1;
    g.posts.add(d.postId);
    if (!g.iconCategory && iconKeyForCategory(d.category)) g.iconCategory = d.category;
  });

  const orderedTitles = [
    ...SECTION_ORDER,
    ...Object.keys(groups).filter((t) => !SECTION_ORDER.includes(t)),
  ];
  return orderedTitles
    .filter((title) => groups[title])
    .map((title) => ({
      key: title,
      label: title,
      icon: iconKeyForCategory(groups[title].iconCategory) ?? null,
      images: groups[title].posts.size,
      // Distinct post ids feeding this group, so "Resolve all" can bulk-review them (endpoint 7).
      postIds: [...groups[title].posts],
      severityCounts: groups[title].severityCounts,
      unclassifiedCount: 0,
    }));
}

// Must match the `--tone-N` modifier count defined on `.bs-detect-table__brand-tile` in brandSafety.scss.
const TRADEMARK_TILE_TONES = 10;

/** Trademark-by-brand table: aggregate placements per brand. */
export function aggregateTrademarkBrands(brands = []) {
  const byBrand = {};
  const order = [];
  brands.forEach(({ brand, placement }) => {
    if (!byBrand[brand]) {
      byBrand[brand] = { detections: 0, primary: 0, active: 0, background: 0 };
      order.push(brand);
    }
    byBrand[brand].detections += 1;
    if (placement === 'primary') byBrand[brand].primary += 1;
    else if (placement === 'active') byBrand[brand].active += 1;
    else if (placement === 'background') byBrand[brand].background += 1;
  });

  return order.map((brand, index) => ({
    key: brand,
    label: brand,
    tone: index % TRADEMARK_TILE_TONES,
    detections: byBrand[brand].detections,
    primary: byBrand[brand].primary || null,
    active: byBrand[brand].active || null,
    background: byBrand[brand].background,
  }));
}

// ─── Server aggregate → view-model builders (ep9 lifetime fields) ─────────────
// The redesigned report endpoint returns authoritative LIFETIME aggregates instead of leaving the FE to
// walk posts[] (now flagged-only + paginated). These map each server field onto the SAME view-model shape
// the post-walk derivations above produce, so the components render unchanged. Category keys are tolerated
// in either spelling the BE uses (`TrademarkFlag` on severityPerFlags, `Trademark` on categorySummary) —
// `flagToCategory` normalizes both to the canonical category.

/**
 * Severity summary cards from `severityPerFlags` (per-category `{ L1..L4 }` post histograms): sum each
 * level across every category into the four buckets. A post flagged in two categories credits both (the
 * BE counts per category's worst severity), so this totals per-(category, post) flags, not distinct posts.
 */
export function severitiesFromSeverityPerFlags(severityPerFlags = {}) {
  const counts = SEVERITY_ORDER.reduce((acc, key) => ({ ...acc, [key]: 0 }), {});
  if (severityPerFlags && typeof severityPerFlags === 'object') {
    Object.values(severityPerFlags).forEach((buckets) => {
      if (!buckets || typeof buckets !== 'object') return;
      Object.entries(buckets).forEach(([level, n]) => {
        const sev = SEVERITY_BY_LEVEL[level];
        if (sev && counts[sev] !== undefined) counts[sev] += Number(n) || 0;
      });
    });
  }
  return SEVERITY_ORDER.map((key) => ({ key, objects: counts[key] }));
}

/**
 * Object Detection table from `categorySummary` (per-category detection counts) + `severityPerFlags`
 * (per-category severity histograms): one row per category — `detections` from categorySummary, `images`
 * (flagged posts in the category) = sum of its severity buckets, `severity` = its worst non-empty level.
 */
export function objectDetectionsFromSummary(categorySummary = {}, severityPerFlags = {}) {
  const byCategory = {};
  const ensure = (rawKey) => {
    const category = flagToCategory(rawKey);
    if (category && !byCategory[category]) {
      byCategory[category] = { detections: 0, images: 0, severity: null };
    }
    return category;
  };

  Object.entries(categorySummary || {}).forEach(([rawKey, n]) => {
    const category = ensure(rawKey);
    if (category) byCategory[category].detections += Number(n) || 0;
  });

  Object.entries(severityPerFlags || {}).forEach(([rawKey, buckets]) => {
    const category = ensure(rawKey);
    if (!category || !buckets || typeof buckets !== 'object') return;
    let worst = null;
    Object.entries(buckets).forEach(([level, n]) => {
      const count = Number(n) || 0;
      if (count <= 0) return;
      byCategory[category].images += count;
      const sev = SEVERITY_BY_LEVEL[level];
      if (sev && (worst === null || SEVERITY_ORDER.indexOf(sev) < SEVERITY_ORDER.indexOf(worst))) {
        worst = sev;
      }
    });
    if (worst) byCategory[category].severity = worst;
  });

  return Object.keys(byCategory)
    .map((category) => ({
      key: category,
      label: categoryLabel(category),
      icon: iconKeyForCategory(category) ?? null,
      severity: byCategory[category].severity ?? SEVERITY.MINOR,
      detections: byCategory[category].detections,
      images: byCategory[category].images,
    }))
    .sort((a, b) => b.detections - a.detections);
}

/**
 * Flag Type table from `severityPerFlags`: group the per-category severity histograms into the canonical
 * sections, summing each section's severity distribution and its flagged-post count. `postIds` is left
 * empty here — the aggregates carry no per-post ids — and is filled from the LOADED posts by
 * `flagTypePostIdsBySection` at render time (so "Resolve all" covers what's on screen). `images` sums the
 * section's category post-counts, so a post flagged in two categories of the same section is counted twice.
 */
export function flagTypesFromSeverityPerFlags(severityPerFlags = {}) {
  const groups = {};
  Object.entries(severityPerFlags || {}).forEach(([rawKey, buckets]) => {
    const category = flagToCategory(rawKey);
    if (!category || !buckets || typeof buckets !== 'object') return;
    const section = CATEGORY_SECTION[category] ?? 'Other';
    if (!groups[section]) groups[section] = { severityCounts: {}, images: 0, iconCategory: null };
    const g = groups[section];
    Object.entries(buckets).forEach(([level, n]) => {
      const count = Number(n) || 0;
      if (count <= 0) return;
      const sev = SEVERITY_BY_LEVEL[level];
      if (!sev) return;
      g.severityCounts[sev] = (g.severityCounts[sev] || 0) + count;
      g.images += count;
    });
    if (!g.iconCategory && iconKeyForCategory(category)) g.iconCategory = category;
  });

  const orderedTitles = [
    ...SECTION_ORDER,
    ...Object.keys(groups).filter((t) => !SECTION_ORDER.includes(t)),
  ];
  return orderedTitles
    .filter((title) => groups[title])
    .map((title) => ({
      key: title,
      label: title,
      icon: iconKeyForCategory(groups[title].iconCategory) ?? null,
      images: groups[title].images,
      postIds: [],
      severityCounts: groups[title].severityCounts,
      unclassifiedCount: 0,
    }));
}

/**
 * Distinct LOADED post ids per flag-type section, keyed by section title. Fills `postIds` on the
 * server-derived flag-type groups so "Resolve all" can bulk-review the on-screen posts of a group (the
 * aggregates carry no ids). Scoped to the posts currently loaded (a page under pagination).
 */
export function flagTypePostIdsBySection(posts = []) {
  const bySection = {};
  (Array.isArray(posts) ? posts : []).forEach((post) => {
    if (post?.id == null) return;
    const sections = new Set();
    postRawCategories(post).forEach((cat) => {
      sections.add(CATEGORY_SECTION[cat] ?? 'Other');
    });
    sections.forEach((section) => {
      if (!bySection[section]) bySection[section] = new Set();
      bySection[section].add(String(post.id));
    });
  });
  const out = {};
  Object.keys(bySection).forEach((section) => {
    out[section] = [...bySection[section]];
  });
  return out;
}

/**
 * Timeline points from the `flaggedPostsByPublishDate` day histogram (`{ 'YYYY-MM-DD': count }`): expand
 * each day into that many `{ ts, flagged: true }` points, oldest-first, so `bucketPublishTimeline`
 * re-buckets them exactly as it does the post-derived timeline. Flagged-only (posts[] is now flagged-only),
 * so every bucket reads as flagged; the axis "Analyzed N" total still comes from `counts.analyzedPosts`.
 */
export function flaggedTimelinePoints(flaggedPostsByPublishDate = {}) {
  const points = [];
  Object.entries(flaggedPostsByPublishDate || {}).forEach(([date, count]) => {
    const parsed = moment(date);
    if (!parsed.isValid()) return;
    const ts = parsed.valueOf();
    const n = Math.max(0, Math.round(Number(count) || 0));
    for (let i = 0; i < n; i += 1) points.push({ ts, flagged: true });
  });
  return points.sort((a, b) => a.ts - b.ts);
}

/**
 * Bucket the `flaggedPostsByPublishDate` day histogram (`{ 'YYYY-MM-DD': count }`) straight into the
 * same `{ buckets, oldestTs, interval }` shape `bucketPublishTimeline` returns — every bucket flagged,
 * since the histogram is flagged-only. Produces output IDENTICAL to
 * `bucketPublishTimeline(flaggedTimelinePoints(histogram))` but without expanding each day into one
 * point per flagged post (a campaign can carry thousands), so there is no per-post allocation or sort.
 * Use this for campaign-scale histograms; the per-post `flaggedTimelinePoints` path still serves the
 * single-influencer report, where the chart also needs the raw points.
 *
 * @returns {{ buckets: Array<{ key, ts, count, flaggedCount, hasFlagged, heightPct }>, oldestTs, interval }}
 */
export function bucketFlaggedHistogram(flaggedPostsByPublishDate = {}) {
  const days = [];
  Object.entries(flaggedPostsByPublishDate || {}).forEach(([date, count]) => {
    const parsed = moment(date);
    if (!parsed.isValid()) return;
    const n = Math.max(0, Math.round(Number(count) || 0));
    if (n === 0) return;
    days.push({ ts: parsed.valueOf(), n });
  });
  if (days.length === 0) return { buckets: [], oldestTs: null, interval: null };

  const oldestTs = days.reduce((min, d) => Math.min(min, d.ts), Infinity);
  const newestTs = days.reduce((max, d) => Math.max(max, d.ts), -Infinity);
  // Interval tracks the content's own date range — same thresholds as `bucketPublishTimeline`.
  const spanDays = (newestTs - oldestTs) / BS_TIMELINE_DAY_MS;
  let interval = 'day';
  if (spanDays > 730) interval = 'month';
  else if (spanDays > 45) interval = 'week';

  const byBucket = new Map();
  days.forEach(({ ts, n }) => {
    const bucketTs = moment(ts)
      .startOf(interval)
      .valueOf();
    const entry = byBucket.get(bucketTs) || { ts: bucketTs, count: 0, flaggedCount: 0 };
    entry.count += n;
    entry.flaggedCount += n;
    byBucket.set(bucketTs, entry);
  });

  const sorted = [...byBucket.values()].sort((a, b) => a.ts - b.ts);
  const maxCount = sorted.reduce((max, b) => Math.max(max, b.count), 0) || 1;
  const buckets = sorted.map((b) => ({
    key: `t-${b.ts}`,
    ts: b.ts,
    count: b.count,
    flaggedCount: b.flaggedCount,
    hasFlagged: b.flaggedCount > 0,
    heightPct: Math.round(12 + (b.count / maxCount) * 88),
  }));

  return { buckets, oldestTs, interval };
}

/**
 * Normalize the ep9 `pagination` block (present only when `page`/`pageSize` were sent) into
 * `{ page, pageSize, total, totalPages, hasMore }`, tolerating the common field-name spellings. Returns
 * null when the response carries no pagination (→ all flagged posts were returned, nothing more to load).
 */
export function readReportPagination(pagination) {
  if (!pagination || typeof pagination !== 'object') return null;
  const num = (...vals) => {
    const found = vals.find((v) => v != null && v !== '' && Number.isFinite(Number(v)));
    return found == null ? null : Number(found);
  };
  const page = num(pagination.page, pagination.currentPage, pagination.current_page) ?? 1;
  const pageSize = num(pagination.pageSize, pagination.perPage, pagination.per_page);
  const total = num(
    pagination.total,
    pagination.totalItems,
    pagination.total_items,
    pagination.count
  );
  const totalPages = num(
    pagination.totalPages,
    pagination.lastPage,
    pagination.last_page,
    pagination.total_pages
  );
  let hasMore = false;
  // The campaign flaggedPosts endpoint (doc #4) sends an explicit `hasMorePages` boolean — trust it first,
  // then fall back to deriving from totalPages/total for the ep9 report block, which carries neither.
  if (typeof pagination.hasMorePages === 'boolean') hasMore = pagination.hasMorePages;
  else if (typeof pagination.has_more_pages === 'boolean') hasMore = pagination.has_more_pages;
  else if (totalPages != null) hasMore = page < totalPages;
  else if (total != null && pageSize) hasMore = page * pageSize < total;
  return { page, pageSize, total, totalPages, hasMore };
}

// ─── Review (per-post) derivations ────────────────────────────────────────────
// The Review tab renders one post at a time from `posts[]` (filtered to `flagged`). These turn the raw
// per-post `safetyDetails` into view-ready detections (with bounding boxes), video segments and a
// transcript.

/**
 * Split text into `{ text, severity }` runs, tagging any run that matches a keyword. Used for the
 * "Why it was flagged" highlight and (later) the transcript. Rendered as `<mark>` spans — no
 * dangerouslySetInnerHTML.
 */
export function splitByKeywords(text, keywords = []) {
  if (!text) return [];
  const list = (Array.isArray(keywords) ? keywords : []).filter((k) => k && k.text);
  if (!list.length) return [{ text, severity: null }];
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const severityByText = new Map(list.map((k) => [k.text.toLowerCase(), k.severity ?? null]));
  const re = new RegExp(`(${list.map((k) => esc(k.text)).join('|')})`, 'gi');
  return text
    .split(re)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, severity: severityByText.get(part.toLowerCase()) ?? null }));
}

/** Worst (most severe) severity present in a list, or minor when empty. */
function worstSeverity(severities = []) {
  return SEVERITY_ORDER.find((s) => severities.includes(s)) ?? SEVERITY.MINOR;
}

/** Image detections split into objects (red boxes / findings) and trademarks (blue boxes). */
export function collectImageDetections(post) {
  const objects = [];
  const trademarks = [];
  (Array.isArray(post?.safetyDetails?.image) ? post.safetyDetails.image : []).forEach((img) => {
    (Array.isArray(img?.imageDetails) ? img.imageDetails : []).forEach((entry, ei) => {
      const rawCategory = flagToCategory(flagKeyOf(entry));
      if (!rawCategory) return;
      const severity = severityFromLevel(entry.severityLevel);
      const isTrademark = rawCategory === 'Trademark';
      const dets = Array.isArray(entry?.imageDetections) ? entry.imageDetections : [];
      // A non-trademark flag with no bounding boxes (real API sends this — e.g. `imageSmokingFlag: 1`
      // with `imageDetections: []`) still needs one item so it surfaces in the gallery badges and
      // findings list; fall back to a single synthetic detection carrying just the category/severity.
      let itemDets = dets;
      if (!dets.length && !isTrademark) itemDets = [null];
      itemDets.forEach((det, i) => {
        const item = {
          key: `${post.creatorPostId}-img-${rawCategory}-${i}`,
          rawCategory,
          category: categoryLabel(rawCategory),
          severity,
          // `||` not `??`: the real API sends an empty-string label, which must fall back to the category.
          label: det?.label || categoryLabel(rawCategory),
          brand: det?.brand ?? null,
          bbox: det?.bbox ?? null,
          // Per-detection crop image (TODO BE: not in the payload yet — see ADDENDUM 14). Tolerant of
          // whichever field name the backend picks; null → the card shows the neutral placeholder.
          thumbnailUrl: det?.cropUrl ?? det?.thumbnailUrl ?? det?.crop ?? null,
          confidence: det?.confidence ?? entry.confidence ?? null,
          explanation: det?.explanation ?? entry.explanation ?? '',
        };
        (isTrademark ? trademarks : objects).push(item);
      });
      // A trademark entry with no bounding boxes yet (real API sends this) instead carries brand hits
      // in `trademarkBrands[]` (same shape the text section uses) — mirror collectTextTrademarks.
      if (isTrademark && !dets.length) {
        (Array.isArray(entry?.trademarkBrands) ? entry.trademarkBrands : []).forEach((tm, i) => {
          const brand = tm.brand ?? null;
          if (!brand) return;
          const label = tm.specificProduct ? `${brand} ${tm.specificProduct}` : brand;
          trademarks.push({
            key: `${post.creatorPostId}-img-tm-${ei}-${i}`,
            rawCategory,
            category: categoryLabel(rawCategory),
            severity,
            label,
            brand,
            placement: tm.placementLevel ?? null,
            bbox: null,
            // Per-trademark crop image (TODO BE: not in the payload yet — see ADDENDUM 14).
            thumbnailUrl: tm.cropUrl ?? tm.thumbnailUrl ?? null,
            confidence: tm.confidence ?? null,
            explanation: entry.explanation ?? '',
          });
        });
      }
    });
  });
  return { objects, trademarks };
}

/**
 * Seconds from a timestamp that may be a number (`15.02`) or an `"HH:MM:SS.mmm"` string
 * (`"00:00:15.056"`), which is how the real API emits `videoTimestamps` ranges. NaN when unparseable.
 */
export function parseTimestampToSeconds(value) {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number') return value;
  const m = /^(\d+):(\d+):(\d+(?:\.\d+)?)$/.exec(String(value).trim());
  if (m) return Number(m[1]) * 3600 + Number(m[2]) * 60 + parseFloat(m[3]);
  const n = Number(value);
  return Number.isNaN(n) ? NaN : n;
}

/** Video time-segments (ranged findings) from `videoDetails[]` + `videoTimestamps`. */
export function collectVideoSegments(post) {
  const segments = [];
  (Array.isArray(post?.safetyDetails?.video) ? post.safetyDetails.video : []).forEach((v) => {
    const timestamps = v?.videoTimestamps ?? {};
    (Array.isArray(v?.videoDetails) ? v.videoDetails : []).forEach((d, i) => {
      if (Number(d?.safetyScore) >= 100) return;
      const rawCategory = flagToCategory(flagKeyOf(d));
      if (!rawCategory) return;
      const flagKey = flagKeyOf(d);
      const ranges = Array.isArray(timestamps[flagKey]) ? timestamps[flagKey] : [];
      // Real ranges carry string `startTimestamp`/`endTimestamp` ("00:00:15.056"), while
      // `timeInSeconds` is a number — so match by parsed-seconds tolerance, not `===`. Fall back to
      // the positional range, then the first, so a segment still gets a real end instead of 0.
      const t = Number(d.timeInSeconds);
      const range =
        ranges.find((r) => Math.abs(parseTimestampToSeconds(r.startTimestamp) - t) < 0.1) ??
        ranges[i] ??
        ranges[0] ??
        null;
      const start = Number.isFinite(t) ? t : parseTimestampToSeconds(range?.startTimestamp) || 0;
      const end = parseTimestampToSeconds(range?.endTimestamp);
      segments.push({
        key: `${post.creatorPostId}-seg-${i}`,
        rawCategory,
        category: categoryLabel(rawCategory),
        severity: severityFromLevel(d.severityLevel),
        // `||` not `??`: real detection labels are empty strings; fall back to the category.
        label: d.label || categoryLabel(rawCategory),
        start,
        end: Number.isFinite(end) ? end : start,
        confidence: d.confidence ?? null,
        explanation: d.explanation ?? '',
        bbox: d.videoDetections?.[0]?.bbox ?? null,
        // Per-detection crop image (TODO BE: not in the payload yet — see ADDENDUM 14).
        thumbnailUrl: d.cropUrl ?? d.thumbnailUrl ?? d.videoDetections?.[0]?.cropUrl ?? null,
      });
    });
  });
  return segments;
}

/**
 * Per-post trademark findings from the TEXT section (`safetyDetails.text[].trademarkBrands[]`) — this
 * is where the real API puts caption/OCR trademark hits (the image section is empty for them). Mirrors
 * the shape of the image-trademark items so the Review rail renders them identically.
 */
export function collectTextTrademarks(post) {
  const trademarks = [];
  (Array.isArray(post?.safetyDetails?.text) ? post.safetyDetails.text : []).forEach((entry, ei) => {
    const severity = severityFromLevel(entry.severityLevel);
    (Array.isArray(entry?.trademarkBrands) ? entry.trademarkBrands : []).forEach((tm, i) => {
      const brand = tm.brand ?? null;
      if (!brand) return;
      const label = tm.specificProduct ? `${brand} ${tm.specificProduct}` : brand;
      trademarks.push({
        key: `${post.creatorPostId}-txt-tm-${ei}-${i}`,
        rawCategory: 'Trademark',
        category: 'Trademark',
        severity,
        label,
        brand,
        placement: tm.placementLevel ?? null,
        bbox: null,
        // Per-trademark crop image (TODO BE: not in the payload yet — see ADDENDUM 14).
        thumbnailUrl: tm.cropUrl ?? tm.thumbnailUrl ?? null,
        confidence: tm.confidence ?? null,
        explanation: '',
      });
    });
  });
  return trademarks;
}

/**
 * Per-post CAPTION/TEXT flags from `safetyDetails.text[]`. The real API flags a post's caption here:
 * each entry carries one `text<Category>Flag`, its `severityLevel`, plus `trademarkBrands[]` and
 * `textKeywords[]`. This surfaces the parts the trademark-only path (`collectTextTrademarks`) drops:
 *   - `objects`  — non-trademark caption flags as boxless detections, so they gain a category, a
 *                  findings card, gallery badges and preference filtering exactly like a boxless image
 *                  flag (mirrors the `collectImageDetections` no-bbox fallback);
 *   - `keywords` — `{ text, severity }` terms to highlight inside the caption (trademark brand names +
 *                  `textKeywords`), de-duped by lowercased text;
 *   - `flagged`  — whether the caption carries any flag at all (drives the "Flagged caption" box).
 * Trademark CARDS stay owned by `collectTextTrademarks`; this never re-emits them.
 *
 * @returns {{
 *   objects: Array<object>,
 *   keywords: Array<{ text: string, severity: string }>,
 *   flagged: boolean,
 * }}
 */
export function collectTextFlags(post) {
  const objects = [];
  const keywords = [];
  const seen = new Set();
  let flagged = false;

  const addKeyword = (raw, severity) => {
    const value = String(raw ?? '').trim();
    if (!value) return;
    const dedupeKey = value.toLowerCase();
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    keywords.push({ text: value, severity });
  };

  (Array.isArray(post?.safetyDetails?.text) ? post.safetyDetails.text : []).forEach((entry, ei) => {
    if (Number(entry?.safetyScore) >= 100) return;
    const rawCategory = flagToCategory(flagKeyOf(entry));
    if (!rawCategory) return;
    flagged = true;
    const severity = severityFromLevel(entry.severityLevel);

    // Non-trademark caption flag → a boxless object detection (parallel to the image boxless fallback).
    if (rawCategory !== 'Trademark') {
      objects.push({
        key: `${post.creatorPostId}-txt-${rawCategory}-${ei}`,
        rawCategory,
        category: categoryLabel(rawCategory),
        severity,
        label: categoryLabel(rawCategory),
        bbox: null,
        thumbnailUrl: null,
        confidence: null,
        explanation: '',
      });
    }

    // Highlightable caption terms: trademark brand names + flagged keywords (tolerant of string or
    // `{ text | keyword }` shapes for `textKeywords`).
    (Array.isArray(entry?.trademarkBrands) ? entry.trademarkBrands : []).forEach((tm) =>
      addKeyword(tm?.brand, severity)
    );
    (Array.isArray(entry?.textKeywords) ? entry.textKeywords : []).forEach((kw) =>
      addKeyword(typeof kw === 'string' ? kw : kw?.text ?? kw?.keyword, severity)
    );
  });

  return { objects, keywords, flagged };
}

/**
 * Transcript block for the video review, or null.
 *
 * Driven by timestamped lines (`{ time (sec), text, severity }`) — that is all the panel renders.
 * Prefer an explicit `transcriptLines`; otherwise derive lines from `transcriptDetections`
 * (`startTimestamp` → seconds, `severityLevel` → severity). NOT gated on `fullTranscript` (the real
 * payload sends it empty), so the box renders the moment the backend supplies lines and stays hidden
 * (the panel's own guard) while there are none.
 *
 * TODO(BE) — ADDENDUM 14: the live payload's transcript is entirely empty. To populate this box the
 * backend must send `transcriptLines: [{ time|startTimestamp, text, severity|severityLevel }]` (or a
 * populated `transcriptDetections` with timestamp + text + severity).
 */
export function buildTranscript(post) {
  const t = post?.safetyDetails?.transcript?.[0];
  if (!t) return null;

  let lines = [];
  if (Array.isArray(t.transcriptLines) && t.transcriptLines.length) {
    lines = t.transcriptLines.map((l) => ({
      time: Number.isFinite(l.time) ? l.time : parseTimestampToSeconds(l.startTimestamp) || 0,
      text: l.text ?? '',
      severity: l.severity ?? (l.severityLevel ? severityFromLevel(l.severityLevel) : null),
    }));
  } else if (Array.isArray(t.transcriptDetections) && t.transcriptDetections.length) {
    lines = t.transcriptDetections
      .filter((d) => d && (d.text || d.transcript))
      .map((d) => ({
        time: parseTimestampToSeconds(d.startTimestamp) || 0,
        text: d.text ?? d.transcript ?? '',
        severity: d.severityLevel ? severityFromLevel(d.severityLevel) : null,
      }));
  }

  if (!lines.length) return null;
  return {
    full: t.fullTranscript ?? '',
    keywords: Array.isArray(t.transcriptKeywords) ? t.transcriptKeywords : [],
    lines,
  };
}

/**
 * One analyzed post → the Review view-model. Carries the real `mediaUrl`/`thumbnailUrl`/`postUrl` for
 * the components to render. Stays pure and testable.
 */
const VIDEO_FILE_PATTERN = /\.(mp4|mov|m4v|webm)(\?|$)/i;
const MEDIA_FILE_PATTERN = /\.(jpe?g|png|gif|webp|heic|heif|bmp|mp4|mov|m4v|webm)(\?|$)/i;

export function normalizeReviewPost(post) {
  if (!post || typeof post !== 'object') return null;
  // Real API sends uppercase `postType` ("IMAGE"/"VIDEO"); mock sends lowercase. Instagram posts can
  // instead carry a feed-placement value ("FEED") that says nothing about the media itself — fall back
  // to the file extension in `mediaUrl`/`thumbnailUrl` for anything that isn't a literal IMAGE/VIDEO.
  const rawPostType = String(post.postType).toLowerCase();
  let type;
  if (rawPostType === 'image') {
    type = 'image';
  } else if (rawPostType === 'video') {
    type = 'video';
  } else {
    type = VIDEO_FILE_PATTERN.test(post.mediaUrl || post.thumbnailUrl || '') ? 'video' : 'image';
  }
  // Some Instagram posts send the post's web page (identical to `postUrl`) in `thumbnailUrl` instead of
  // a real asset — not a file the `<img>`/`<video>` can render. Drop it so consumers' `thumbnailUrl ||
  // mediaUrl` fallback reaches the real file.
  const thumbnailUrl = MEDIA_FILE_PATTERN.test(post.thumbnailUrl || '') ? post.thumbnailUrl : null;
  const { objects: imageObjects, trademarks: imageTrademarks } = collectImageDetections(post);
  const textFlags = collectTextFlags(post);
  // Tag each detection with the modality it came from, so the gallery chip's hover popover can say
  // whether a hit was in the image, caption/text or video. Purely descriptive — nothing downstream
  // (filters, counts, severity) branches on it.
  // Stamp each flagged item with its persisted verdict from `post.detectionReviews[]` (endpoint 6),
  // matched by tuple identity — so an object/trademark/segment approved in a prior session reads as
  // Approved on load, instead of resetting to pending. `withSource` runs first because `source` is part
  // of that tuple.
  const reviewIndex = buildDetectionReviewIndex(post.detectionReviews);
  const withSource = (source) => (d) => {
    const withSrc = { ...d, source };
    const reviewAction = detectionReviewAction(reviewIndex, withSrc);
    return reviewAction ? { ...withSrc, reviewAction } : withSrc;
  };
  // Non-trademark caption flags surface as boxless objects; image objects stay first so an image flag
  // keeps category priority, while a caption-only post still gets a real category from its text flag.
  const objects = [
    ...imageObjects.map(withSource('image')),
    ...textFlags.objects.map(withSource('text')),
  ];
  // Real trademark hits arrive in the text section, not the image section — merge both.
  const trademarks = [
    ...imageTrademarks.map(withSource('image')),
    ...collectTextTrademarks(post).map(withSource('text')),
  ];
  const segments = (type === 'video' ? collectVideoSegments(post) : []).map(withSource('video'));
  const transcript = type === 'video' ? buildTranscript(post) : null;

  // Two severities, on purpose (AAPU-6742):
  //  • `severity` — the worst of the post's own detections (floored by the backend post-level value).
  //    This drives the Review's Severity filter and Find Similar, so those find a post by the most
  //    severe thing in it, matching the Report's per-detection grading.
  //  • `reportedSeverity` — the backend's official post-level rating (which can under-report vs the
  //    detections). The gallery badge shows THIS, so the card reflects the backend's own verdict while
  //    the per-detection severities stay visible in the chip's hover popover.
  const detectionSeverities = [...objects, ...trademarks, ...segments].map((d) => d.severity);
  const severity = worstSeverity(
    post.severityLevel
      ? [severityFromLevel(post.severityLevel), ...detectionSeverities]
      : detectionSeverities
  );
  const reportedSeverity = post.severityLevel ? severityFromLevel(post.severityLevel) : severity;
  const primary = objects[0] ?? segments.find((s) => s.rawCategory !== 'Trademark') ?? null;
  let category = null;
  if (primary) category = primary.category;
  else if (trademarks.length) category = 'Trademark';
  // Distinct flag categories for the gallery badges — Trademark has its own "N TM" badge, so it's
  // excluded here rather than shown twice.
  const flagCategories = [
    ...new Set(
      [...objects, ...segments].filter((d) => d.rawCategory !== 'Trademark').map((d) => d.category)
    ),
  ];

  return {
    id: post.creatorPostId,
    type,
    name: '',
    mediaUrl: post.mediaUrl ?? null,
    thumbnailUrl,
    postUrl: post.postUrl ?? null,
    // When the content was published (backend `publishDate`) — drives the report timeline's date axis.
    publishDate: post.publishDate ?? null,
    durationSeconds: Number(post.durationSeconds) || null,
    caption: post.caption ?? '',
    // Caption/text-flag overlay: whether the caption is flagged, and the terms to highlight in it.
    captionFlagged: textFlags.flagged,
    captionKeywords: textFlags.keywords,
    // Real API carries the verdict as `review.action`; there is no `reviewStatus` field.
    status: post.review?.action ?? post.reviewStatus ?? REVIEW_STATUS.PENDING,
    severity,
    // Backend post-level rating, for the gallery badge (see the two-severities note above).
    reportedSeverity,
    category,
    explanation: primary ? primary.explanation : '',
    issueCount: objects.length + trademarks.length + segments.length,
    trademarkCount:
      trademarks.length + segments.filter((s) => s.rawCategory === 'Trademark').length,
    flagCategories,
    objects,
    trademarks,
    segments,
    transcript,
  };
}

/**
 * Endpoint 11 (AAPU-6775) → the Bulk Approve modal's tiles: every occurrence of ONE flag across a
 * whole scope (a creator report, or a campaign). Each item becomes the pair the modal and the review
 * view already speak — a minimal `post` (so a tile can render and a resolve can target the right
 * creator) plus a normalized `detection` shaped exactly like `normalizeReviewPost`'s, so the same
 * crop/severity/label rendering works on both.
 *
 * `detection.key` is `${postTableId}::${detectionIdentityKey(item)}`, which stays unique once the
 * same flag appears on many posts (the per-post tuple alone would collide). The BE's own
 * `detectionKey` hash rides along as `detection.detectionKey` but is NOT the FE identity — every
 * other review surface is keyed by `key`. `status` follows the review view's own precedence: the
 * post-level verdict wins, then the occurrence's own verdict, then pending. Defensive throughout — a
 * missing/odd payload yields `[]`, never a throw.
 *
 * `items` is capped at `BS_FLAG_OCCURRENCES_MAX_ITEMS` (15) while `data.total` carries the scope's
 * true count, so this list is a SAMPLE whenever `total` exceeds the cap — the caller must read
 * `total`, not `items.length`, before deciding to render tiles (see the ep11 contract block).
 *
 * @param {object} data  the ep11 `resp.data` payload
 * @returns {Array<{ post: object, detection: object, status: string }>}
 */
export function normalizeFlagOccurrences(data) {
  const items = Array.isArray(data?.items) ? data.items : [];
  return items
    .filter((item) => item && typeof item === 'object' && item.postTableId != null)
    .map((item) => {
      const rawPostType = String(item.postType).toLowerCase();
      let type;
      if (rawPostType === 'image') {
        type = 'image';
      } else if (rawPostType === 'video') {
        type = 'video';
      } else {
        type = VIDEO_FILE_PATTERN.test(item.postMediaUrl || item.postThumbnailUrl || '')
          ? 'video'
          : 'image';
      }
      // Same guard as `normalizeReviewPost`: some posts send their web page in `thumbnailUrl`, which
      // no <img>/<video> can render — drop it so the `thumbnailUrl || mediaUrl` fallback reaches the file.
      const postThumbnailUrl = MEDIA_FILE_PATTERN.test(item.postThumbnailUrl || '')
        ? item.postThumbnailUrl
        : null;
      const rawCategory = item.category ?? null;
      const start =
        item.timeInSeconds == null || Number.isNaN(Number(item.timeInSeconds))
          ? null
          : Number(item.timeInSeconds);
      const status = item.postAction ?? item.reviewAction ?? REVIEW_STATUS.PENDING;
      return {
        post: {
          id: String(item.postTableId),
          creatorReportDetailsId: item.creatorReportDetailsId ?? null,
          type,
          mediaUrl: item.postMediaUrl ?? null,
          thumbnailUrl: postThumbnailUrl,
        },
        detection: {
          key: `${item.postTableId}::${detectionIdentityKey({
            source: item.source,
            category: rawCategory,
            label: item.label,
            brand: item.brand,
            timeInSeconds: item.timeInSeconds,
          })}`,
          // The BE's own sha-256 of the detection tuple. Carried through for correlation/debugging
          // against the server's records; `key` above stays the FE's identity because the overlay,
          // the AI-findings rail and the review payload are all keyed by it on every other surface.
          detectionKey: item.detectionKey ?? null,
          source: item.source ?? null,
          rawCategory,
          category: rawCategory ? categoryLabel(rawCategory) : null,
          label: item.label ?? null,
          brand: item.brand ?? null,
          start,
          // Raw ML level (`L1`..`L4`) like the rest of the API; `severityFromLevel` maps it. A missing
          // level reads as no severity (no dot on the tile) rather than defaulting to minor.
          severity: item.severityLevel != null ? severityFromLevel(item.severityLevel) : null,
          confidence: item.confidence ?? null,
          thumbnailUrl: item.thumbnailUrl ?? null,
          reviewAction: item.reviewAction ?? null,
        },
        // Anything the BE sends that isn't a REVIEW_STATUS value reads as still-to-do, so an
        // unexpected verdict can never lock a tile out of the reviewer's reach.
        status: Object.values(REVIEW_STATUS).includes(status) ? status : REVIEW_STATUS.PENDING,
      };
    });
}

/**
 * Distinct flag categories on one normalized review post, each with its detection count, its worst
 * severity, and the individual detections that make it up (`items`), in first-seen order (objects →
 * trademarks → segments). A category spanning more than one section (e.g. an object and a video
 * segment) is merged into a single tag. The Media Gallery renders a neutral chip per category and, on
 * hover, a popover that lists `items` — each with its own severity colour and the modality it came
 * from (image / text / video).
 * @returns {Array<{
 *   label: string,
 *   count: number,
 *   severity: string,
 *   items: Array<{ label: string, severity: string, source: string|null }>,
 * }>}
 */
export function postFlagTags(post) {
  if (!post || typeof post !== 'object') return [];
  const order = [];
  const groups = {};
  const bump = (label, detection) => {
    if (!label) return;
    if (!groups[label]) {
      groups[label] = { items: [] };
      order.push(label);
    }
    groups[label].items.push({
      label: detection.label || label,
      severity: detection.severity,
      source: detection.source ?? null,
    });
  };
  (post.objects || []).forEach((o) => bump(o.category, o));
  (post.trademarks || []).forEach((t) => bump(t.category || 'Trademark', t));
  (post.segments || []).forEach((s) => bump(s.category, s));
  return order.map((label) => ({
    label,
    count: groups[label].items.length,
    severity: worstSeverity(groups[label].items.map((it) => it.severity)),
    items: groups[label].items,
  }));
}

/**
 * Tally review status across posts, honoring an overlay of user decisions (postId → status).
 * @returns {{ all: number, pending: number, approved: number, rejected: number }}
 */
export function reviewStatusCounts(posts = [], overlay = {}) {
  const counts = { all: 0, pending: 0, approved: 0, rejected: 0 };
  (Array.isArray(posts) ? posts : []).forEach((p) => {
    const status = overlay[p.id] ?? p.status ?? REVIEW_STATUS.PENDING;
    counts.all += 1;
    if (counts[status] !== undefined) counts[status] += 1;
  });
  return counts;
}

// ─── Normalization ────────────────────────────────────────────────────────────

/**
 * Adapt the API report object (endpoint 9, `GET /creator/brand-safety/report/{crd}`) into the shape
 * the report components consume. `identityFallback` (the clicked row's platform/username/name/avatar)
 * fills any gap in `creator`.
 *
 * Also accepts the older endpoint-6 "check" shape (status/completedAt/createdAt/startedAt flat on the
 * object, no `lastCheck`) so existing fixtures/tests keep working — `lastCheck` is preferred when
 * present, everything else falls back to the flat fields. Everything is defaulted, so a partial/
 * unsettled report (or a response missing a whole section) degrades to empty sections rather than a
 * crash.
 */
export function normalizeBrandSafetyReport(check, identityFallback = {}) {
  if (!check || typeof check !== 'object') return null;

  const counts = check.counts ?? {};
  const creator = check.creator ?? {};
  const lastCheck = check.lastCheck ?? {};
  const reviewSummary = check.reviewSummary ?? null;

  // Prefer the backend's authoritative LIFETIME aggregates (redesigned ep9). Fall back to walking the
  // returned posts client-side for older responses that don't carry them yet (BE mid-rollout / fixtures /
  // tests) — but note posts[] is now flagged-only + paginated, so the aggregate path is the accurate one.
  const hasServerAggregates = check.severityPerFlags != null || check.categorySummary != null;

  // Trademark-by-brand has no lifetime server aggregate, so it (and the raw brands it needs) is still
  // derived from the LOADED posts — page-scoped under pagination (see BE follow-up).
  const { detections, brands } = collectCheckDetections(check);

  // Review workload for the status bar / badge: prefer `reviewSummary` (the field the BE documents for
  // "how much left to review"); fall back to `counts`.
  const statusCounts = {
    [REVIEW_STATUS.APPROVED]: Number(reviewSummary?.approved ?? counts.approved) || 0,
    [REVIEW_STATUS.PENDING]: Number(reviewSummary?.pending ?? counts.pending) || 0,
    [REVIEW_STATUS.REJECTED]: Number(reviewSummary?.rejected ?? counts.rejected) || 0,
  };

  const name = creator.name || identityFallback.name || null;
  const username = creator.username || identityFallback.username || null;
  const platform = creator.socialNetwork || identityFallback.platform || null;
  const influencer =
    name || username || platform
      ? {
          name,
          username,
          platform,
          avatarUrl: creator.photoUrl || identityFallback.avatarUrl || null,
          followers: creator.followers ?? null,
          engagementRate: creator.engagementRate ?? null,
        }
      : null;

  // Flagged-only page of posts (the Review gallery). Under pagination this is one page; the summary
  // widgets below come from the lifetime aggregates, not this slice, so they stay accurate.
  const posts = (Array.isArray(check.posts) ? check.posts.filter((p) => p?.flagged) : [])
    .map(normalizeReviewPost)
    .filter(Boolean);

  const severities = hasServerAggregates
    ? severitiesFromSeverityPerFlags(check.severityPerFlags)
    : deriveSeverityObjects(detections);
  const flagTypes = hasServerAggregates
    ? flagTypesFromSeverityPerFlags(check.severityPerFlags)
    : deriveFlagTypeGroups(detections);
  const objectDetections = hasServerAggregates
    ? objectDetectionsFromSummary(check.categorySummary, check.severityPerFlags)
    : deriveObjectDetections(detections);
  // Timeline: the server's flagged-post day histogram when present, else the post-walk (all analyzed).
  const timeline = check.flaggedPostsByPublishDate
    ? flaggedTimelinePoints(check.flaggedPostsByPublishDate)
    : buildPublishTimeline(check.posts);

  return {
    checkId: check.checkId ?? null,
    analysisState: mapCheckStatus(lastCheck.status ?? check.status),
    // Why a failed check failed (AAPU-6694) — passthrough for a failed-report state; map with
    // `brandSafetyFailureReason` before display so provider/internal names never reach the UI.
    failureReason:
      lastCheck.failureReason ??
      check.failureReason ??
      lastCheck.failure_reason ??
      check.failure_reason ??
      null,
    analyzedAt:
      lastCheck.completedAt ?? check.completedAt ?? lastCheck.createdAt ?? check.createdAt ?? null,
    analyzedCount: Number(counts.analyzedPosts) || 0,
    // Lifetime flagged-post total (drives the Review badge / "of N" under pagination); falls back to the
    // loaded count for the pre-aggregate response shape.
    flaggedCount: Number(counts.flaggedPosts) || posts.length || 0,
    safetyScore: check.overallSafetyScore ?? null,
    // The analysis RUN date — the fallback for the timeline axis start when the backend can't supply the
    // oldest post's publish date (below) and there are no per-post dates to derive it from.
    firstAnalyzedAt:
      lastCheck.startedAt ?? check.startedAt ?? lastCheck.createdAt ?? check.createdAt ?? null,
    // The exact publish date of the OLDEST analyzed post (backend `firstAnalyzedPostPublishDate`) — the
    // true content-start for the timeline axis, so the FE no longer computes min(publishDate) itself.
    firstAnalyzedPostPublishDate: check.firstAnalyzedPostPublishDate ?? null,
    statusCounts,
    reviewQueueCount: Number(reviewSummary?.pending ?? counts.pending) || 0,
    // Flagged counts by modality (textFlagged / videoFlagged / …) — passthrough for callers that show it.
    summary: check.summary ?? null,
    influencer,
    severities,
    flagTypes,
    objectDetections,
    trademarkBrands: aggregateTrademarkBrands(brands),
    // Raw detections/brands from the LOADED posts. No longer used to re-derive the summary widgets (those
    // are lifetime aggregates now — see Q2), but kept for the trademark-by-brand table (which has no server
    // aggregate) to accumulate as more pages load, plus tests/debug. Page-scoped under pagination.
    detections,
    brands,
    posts,
    timeline,
    // Pagination for the flagged-posts list (null when the response returned all posts at once).
    pagination: readReportPagination(check.pagination),
  };
}

/**
 * The one-line verdict under the report title, built from the API's `overallSafetyScore`
 * (higher = safer). Falls back to a neutral sentence when the score or name is missing.
 */
export function buildSafetySummary(report) {
  const name = report?.influencer?.name;
  const score = report?.safetyScore;
  const subject = name || 'This profile';
  if (score === null || score === undefined) {
    return `${subject} has not been scored yet. Check back once the analysis completes.`;
  }
  const rounded = Math.round(Number(score) * 10) / 10;

  if (rounded === 100) {
    return `${subject} received an overall safety score of ${rounded}.`;
  }
  return `${subject} received an overall safety score of ${rounded}. The profile requires review before campaign approval.`;
}

/** A report is renderable once it carries identity and did not fail. */
export function hasBrandSafetyReport(report) {
  return Boolean(report) && report.analysisState !== 'failed' && Boolean(report.influencer);
}

// Brand Safety Parameters default/filter state now comes from the shared campaign-creation helpers
// (`getDefaultPreferences` / `setSeverity` in Persona/BrandSafety/brandSafetySeverity.js), so there
// is no module-local default builder here.
